import React from "react";
import Carousel from "react-material-ui-carousel";
import {
  Fade,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import debounce from "lodash/debounce";
import imageCredits from "core/assets/image_credits.json";
import ProgressiveImage from "react-progressive-graceful-image";

const debounceCall = debounce((loadingRef, setDisplay) => {
  if (loadingRef.current) {
    loadingRef.current = false;
    setDisplay(true);
  }
}, 200);

const NuclideUsageDetail = ({ selectedNuclide }) => {
  const items = selectedNuclide?.getUses?.() ?? [];
  const elementName = selectedNuclide?.getElementName?.();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [display, setDisplay] = React.useState(false);
  const loadingRef = React.useRef(false);

  React.useEffect(
    () => () => {
      loadingRef.current = false;
    },
    []
  );
  React.useEffect(() => {
    // debounce display to enhance scrolling performance
    if (!loadingRef.current) {
      setDisplay(false);
    }

    loadingRef.current = true;
    debounceCall(loadingRef, setDisplay);
  }, [elementName]);

  const [isOnline, setIsOnline] = React.useState(window.navigator.onLine);
  React.useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  if (isDesktop && !display) {
    return (
      <Stack
        height="460px"
        justifyContent="center"
        alignItems="center"
        px="100px"
      >
        <Skeleton width="100%" height="50px" />
        <br />
        <Skeleton width="100%" height="150px" variant="rectangle" />
        <br />
        <Skeleton width="100%" />
        <Skeleton width="100%" />
        <Skeleton width="100%" />
      </Stack>
    );
  }

  const lowerElementName = elementName?.toLowerCase();

  const elementSrc = `/elements/${lowerElementName}.jpg`;
  return (
    <Fade in>
      <Stack
        alignItems="center"
        p="10px 10px 10px 10px"
        minHeight="440px"
        spacing={2}
        overflow="hidden"
      >
        <Typography variant="h5" fontWeight="bold">
          {selectedNuclide.name}
        </Typography>
        <>
          {isOnline ? (
            <ProgressiveImage
              src={elementSrc}
              placeholder={`/elements/_placeholder/${lowerElementName}.jpg`}
            >
              {(src) => (
                <Paper
                  component="img"
                  elevation={10}
                  height="165px"
                  width="auto"
                  src={src}
                  alt="Element not found"
                />
              )}
            </ProgressiveImage>
          ) : (
            <Paper
              component="img"
              elevation={10}
              height="165px"
              width="auto"
              src={elementSrc}
              alt="Element not found"
            />
          )}
          <Typography variant="caption" textAlign="center">
            Image Source: {imageCredits[lowerElementName]}
          </Typography>
        </>
        <Carousel
          sx={{
            width: "100%",
            cursor: "grab",
          }}
          navButtonsAlwaysVisible={isDesktop}
          stopAutoPlayOnHover
          autoPlay
          interval={10000}
          animation="slide"
        >
          {items.map((description, i) => (
            <Stack
              justifyContent="center"
              width="calc(100% - 100px)"
              px="50px"
              minHeight="100px"
              key={i}
            >
              <Typography variant="h6" textAlign="center">
                {description}
              </Typography>
            </Stack>
          ))}
        </Carousel>
      </Stack>
    </Fade>
  );
};

export default React.memo(NuclideUsageDetail);
